import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './CareerDetail.css';
import Header from '../common/header/Header';
import { opportunitiesData } from '../data/OpportunitiesData';

const CareerDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const data = opportunitiesData.find(o => o.id === parseInt(id));
    setItem(data || null);
    setLoading(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const isJobActive = (deadline) => new Date(deadline) >= new Date();

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    });

  const getDaysRemaining = (deadline) =>
    Math.ceil((new Date(deadline) - new Date()) / (1000 * 3600 * 24));

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="error-container">
        <h2>Opportunity Not Found</h2>
        <Link to="/careers" className="btn-apply-now">Back to Opportunities</Link>
      </div>
    );
  }

  const active = isJobActive(item.applicationDeadline);
  const daysLeft = getDaysRemaining(item.applicationDeadline);
  const isConsultancy = item.type === 'Consultancy';

  // Build content sections
  const sections = [];
  let sectionNum = 1;

  if (item.introduction || item.background) {
    sections.push({
      num: `${sectionNum++}`,
      heading: isConsultancy ? 'Introduction' : 'Background',
      content: item.introduction || item.background,
      type: 'text'
    });
  }
  if (item.contentScope) {
    sections.push({
      num: `${sectionNum++}`,
      heading: 'Content for the Scope of Work',
      content: item.contentScope,
      type: 'text'
    });
  }
  if (item.objectives || item.purpose) {
    sections.push({
      num: `${sectionNum++}`,
      heading: isConsultancy ? 'Objectives of the Assignment' : 'Purpose of the Position',
      content: item.objectives || item.purpose,
      type: 'text'
    });
  }
  if (item.keyTasks || item.responsibilities) {
    sections.push({
      num: `${sectionNum++}`,
      heading: isConsultancy ? 'Key Tasks' : 'Key Responsibilities',
      tasks: item.keyTasks || null,
      responsibilities: item.responsibilities || null,
      type: 'tasks'
    });
  }
  if (item.deliverables) {
    sections.push({
      num: `${sectionNum++}`,
      heading: 'Expected Deliverables',
      items: item.deliverables,
      type: 'deliverables'
    });
  }
  if (item.timeFrame) {
    sections.push({
      num: `${sectionNum++}`,
      heading: 'Time Frame',
      content: item.timeFrame,
      type: 'text'
    });
  }
  if (item.qualifications) {
    sections.push({
      num: `${sectionNum++}`,
      heading: isConsultancy ? 'Required Qualifications & Experience' : 'Qualifications',
      tasks: item.qualifications,
      type: 'tasks'
    });
  }

  const deliverableIcons = ['📄', '📊', '✅', '📋', '📝', '📌'];

  return (
    <div className="opportunity-detail-page">
      <Header />

      {/* ── HERO ── */}
      <section className="detail-hero">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/careers">Opportunities</Link>
            <span>/</span>
            <span className="current">{item.title}</span>
          </nav>

          <div className="detail-header-layout">
            <div className="detail-header-left">
              <div className="detail-type-badge">{item.type}</div>
              <h1>{item.title}</h1>
              <div className="detail-quick-meta">
                <span><i className="fas fa-map-marker-alt" /> {item.location}</span>
                <span><i className="fas fa-clock" /> {item.duration}</span>
                <span><i className="fas fa-tag" /> {item.category}</span>
                {item.applicationDeadline && (
                  <span><i className="fas fa-calendar-alt" /> Deadline: {formatDate(item.applicationDeadline)}</span>
                )}
              </div>
            </div>

            {(item.donorLogo || item.logoa) && (
              <div className="detail-donor-card">
                <p className="donor-by">Supported by</p>
                <div className="donor-logos-container">
                  {item.donorLogo && <img src={item.donorLogo} alt={item.donor} />}
                  {item.logoa && <img src={item.logoa} alt="FRT Logo" />}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="detail-main-section">
        <div className="container">
          <div className="detail-layout">

            {/* Left — Content sections */}
            <div className="detail-content">
              {sections.map(sec => (
                <div key={sec.num} className={`content-block${sec.type === 'application' ? ' application-block' : ''}`}>
                  <div className="section-heading">
                    <div className="section-number">{sec.num}</div>
                    <h2>{sec.heading}</h2>
                  </div>

                  {sec.type === 'text' && <p>{sec.content}</p>}

                  {sec.type === 'tasks' && (
                    <ul className="tor-list">
                      {(sec.tasks || []).map((task, i) => (
                        <li key={i}>
                          <span className="li-marker">{i + 1}</span>
                          <span>{task}</span>
                        </li>
                      ))}
                      {(sec.responsibilities || []).map((group, gi) => (
                        <React.Fragment key={gi}>
                          {group.tasks.map((task, ti) => (
                            <li key={ti}>
                              <span className="li-marker">{ti + 1}</span>
                              <span>{task}</span>
                            </li>
                          ))}
                        </React.Fragment>
                      ))}
                    </ul>
                  )}

                  {sec.type === 'deliverables' && (
                    <div className="deliverables-grid">
                      {sec.items.map((d, i) => (
                        <div key={i} className="deliverable-item">
                          <span className="deliverable-icon">{deliverableIcons[i] || '📄'}</span>
                          <span>{d}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Application Process */}
              <div className="content-block application-block">
                <div className="section-heading">
                  <div className="section-number" style={{ background: 'rgba(82,183,136,0.3)' }}>✉</div>
                  <h2>Application Process</h2>
                </div>
                <p>{item.applicationProcess.split('\n')[0]}</p>
                <div className="application-details">
                  <div className="application-detail-row">
                    <i className="fas fa-envelope" />
                    <a href="mailto:info@farmradiomw.org">info@farmradiomw.org</a>
                  </div>
                  <div className="application-detail-row">
                    <i className="fas fa-map-marker-alt" />
                    <span>Farm Radio Trust Offices, Area 47 Sector 4, Lilongwe 3</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Sidebar */}
            <aside className="detail-sidebar">
              <div className="sidebar-card">
                <p className="sidebar-card-title">Opportunity Summary</p>
                <div className="summary-grid">
                  <div className="summary-row">
                    <label>Type</label>
                    <span>{item.subType}</span>
                  </div>
                  <div className="summary-row">
                    <label>Category</label>
                    <span>{item.category}</span>
                  </div>
                  <div className="summary-row">
                    <label>Duration</label>
                    <span>{item.duration}</span>
                  </div>
                  <div className="summary-row">
                    <label>Location</label>
                    <span>{item.location}</span>
                  </div>
                  <div className="summary-row">
                    <label>Deadline</label>
                    <span className={active ? 'deadline-active-text' : 'deadline-expired-text'}>
                      {formatDate(item.applicationDeadline)}
                      {active && daysLeft <= 14 && (
                        <> &nbsp;·&nbsp; <strong>{daysLeft} days left</strong></>
                      )}
                    </span>
                  </div>
                </div>

                {active ? (
                  <a
                    href={`mailto:info@farmradiomw.org?subject=Application: ${item.title}`}
                    className="btn-apply-now"
                  >
                    Apply Now <i className="fas fa-paper-plane" />
                  </a>
                ) : (
                  <div className="closed-notice">Applications Closed</div>
                )}
              </div>

              <div className="sidebar-help">
                <div className="sidebar-help-icon">
                  <i className="fas fa-question" />
                </div>
                <h4>Need more information?</h4>
                <p>Our team is happy to answer any questions about this opportunity.</p>
                <a href="mailto:info@farmradiomw.org">
                  <i className="fas fa-envelope" /> Contact Us
                </a>
              </div>
            </aside>

          </div>
        </div>
      </section>
    </div>
  );
};

export default CareerDetail;
